// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import ContactRelationship from "./contact-relationship.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": true
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": true
    },
    "identifier": {
      "column": {
        "default": null,
        "name": "identifier",
        "null": false,
        "type": "string"
      },
      "name": "identifier",
      "selected_by_default": null,
      "translated": false
    },
    "translated_identifier": {
      "column": null,
      "name": "translated_identifier",
      "selected_by_default": null,
      "translated": false
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": true
    },
    "name": {
      "column": null,
      "name": "name",
      "selected_by_default": false,
      "translated": true
    },
    "name_da": {
      "column": null,
      "name": "name_da",
      "selected_by_default": false,
      "translated": false
    },
    "name_en": {
      "column": null,
      "name": "name_en",
      "selected_by_default": false,
      "translated": false
    },
    "name_no": {
      "column": null,
      "name": "name_no",
      "selected_by_default": false,
      "translated": false
    },
    "name_se": {
      "column": null,
      "name": "name_se",
      "selected_by_default": false,
      "translated": false
    }
  },
  "className": "ContactRelationshipType",
  "collectionKey": "contact_relationship_types",
  "collectionName": "contact_relationship_types",
  "i18nKey": "contact_relationship_type",
  "camelizedLower": "contactRelationshipType",
  "name": "ContactRelationshipType",
  "nameDasherized": "contact-relationship-type",
  "pluralName": "contact_relationship_types",
  "ransackable_associations": [
    {
      "className": "ContactRelationshipType::Audit",
      "collectionName": "contact_relationship_type_audits",
      "foreignKey": "contact_relationship_type_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "ContactRelationshipTypeAudit",
      "through": null
    },
    {
      "className": "ContactRelationship",
      "collectionName": "contact_relationships",
      "foreignKey": "contact_relationship_type_id",
      "name": "contact_relationships",
      "macro": "has_many",
      "resource_name": "ContactRelationship",
      "through": null
    },
    {
      "className": "ContactRelationshipType::Audit",
      "collectionName": "contact_relationship_type_audits",
      "foreignKey": "contact_relationship_type_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "ContactRelationshipTypeAudit",
      "through": null
    },
    {
      "className": "ContactRelationshipType::Translation",
      "collectionName": null,
      "foreignKey": "contact_relationship_type_id",
      "name": "current_translation",
      "macro": "has_one",
      "resource_name": null,
      "through": null
    },
    {
      "className": "ContactRelationshipType::Translation",
      "collectionName": null,
      "foreignKey": "contact_relationship_type_id",
      "name": "translations",
      "macro": "has_many",
      "resource_name": null,
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "identifier",
      "column": {
        "default": null,
        "name": "identifier",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "ContactRelationship",
      "collectionName": "contact_relationships",
      "foreignKey": "contact_relationship_type_id",
      "name": "contact_relationships",
      "macro": "has_many",
      "resource_name": "ContactRelationship",
      "through": null
    }
  ],
  "paramKey": "contact_relationship_type",
  "primaryKey": "id"
}

/** Frontend model for ContactRelationshipType. */
export default class ContactRelationshipType extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  identifier() {
    return this.readAttributeUnderscore("identifier")
  }

  /** @returns {boolean} */
  hasIdentifier() {
    const value = this.identifier()

    return this._isPresent(value)
  }

  /** @returns {any} */
  translatedIdentifier() {
    return this.readAttributeUnderscore("translated_identifier")
  }

  /** @returns {boolean} */
  hasTranslatedIdentifier() {
    const value = this.translatedIdentifier()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameDa() {
    return this.readAttributeUnderscore("name_da")
  }

  /** @returns {boolean} */
  hasNameDa() {
    const value = this.nameDa()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameEn() {
    return this.readAttributeUnderscore("name_en")
  }

  /** @returns {boolean} */
  hasNameEn() {
    const value = this.nameEn()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameNo() {
    return this.readAttributeUnderscore("name_no")
  }

  /** @returns {boolean} */
  hasNameNo() {
    const value = this.nameNo()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameSe() {
    return this.readAttributeUnderscore("name_se")
  }

  /** @returns {boolean} */
  hasNameSe() {
    const value = this.nameSe()

    return this._isPresent(value)
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./contact-relationship.js").default>} */
  contactRelationships() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["contact_relationship_type_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "contact_relationships",
        model: this,
        modelClass: ContactRelationship
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./contact-relationship.js").default>>} */
  loadContactRelationships() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["contact_relationship_type_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "contact_relationships",
        model: this,
        modelClass: ContactRelationship
      },
      {ransack}
    )
  }
}
